import React, { useState } from "react";
import { Menu, Close, LightMode, DarkMode } from "@mui/icons-material";
import { useTheme } from "../contexts/ThemeContext";

interface HeaderProps {
  onHomeClick: () => void;
  onAboutClick: () => void;
  onAdminClick: () => void;
  onSignOut?: () => void;
  isLoggedIn?: boolean;
}

export default function Header({
  onHomeClick,
  onAboutClick,
  onAdminClick,
  onSignOut,
  isLoggedIn = false,
}: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <header className="sticky top-0 z-20 border-b border-neutral-200/80 bg-stone-50/88 backdrop-blur-xl transition-colors dark:border-neutral-800/80 dark:bg-neutral-950/86">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-[68px] items-center justify-between">
          <button
            onClick={onHomeClick}
            className="group flex items-center gap-3"
            aria-label="Go to home"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-300 bg-white text-sm font-semibold text-neutral-950 shadow-sm transition-smooth group-hover:border-primary-300 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-50">
              AK
            </span>
            <span className="text-base font-semibold tracking-tight text-neutral-950 transition-colors group-hover:text-primary-700 dark:text-neutral-50 dark:group-hover:text-primary-300 sm:text-lg">
              Arvind KC
            </span>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            <button
              onClick={onHomeClick}
              className="rounded-full px-4 py-2 text-sm font-medium text-neutral-600 transition-smooth hover:bg-white hover:text-neutral-950 active:scale-[0.98] dark:text-neutral-400 dark:hover:bg-neutral-900 dark:hover:text-neutral-100"
            >
              Home
            </button>
            <button
              onClick={onAboutClick}
              className="rounded-full px-4 py-2 text-sm font-medium text-neutral-600 transition-smooth hover:bg-white hover:text-neutral-950 active:scale-[0.98] dark:text-neutral-400 dark:hover:bg-neutral-900 dark:hover:text-neutral-100"
            >
              About
            </button>
            {isLoggedIn && (
              <button
                onClick={onAdminClick}
                className="rounded-full px-4 py-2 text-sm font-medium text-neutral-600 transition-smooth hover:bg-white hover:text-neutral-950 active:scale-[0.98] dark:text-neutral-400 dark:hover:bg-neutral-900 dark:hover:text-neutral-100"
              >
                Admin
              </button>
            )}
            {isLoggedIn && onSignOut && (
              <button
                onClick={onSignOut}
                className="rounded-full px-4 py-2 text-sm font-medium text-red-600 transition-smooth hover:bg-red-50 hover:text-red-700 active:scale-[0.98] dark:text-red-400 dark:hover:bg-red-950/30 dark:hover:text-red-300"
              >
                Logout
              </button>
            )}

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="ml-2 rounded-full border border-neutral-200 bg-white p-2 text-neutral-600 shadow-sm transition-smooth hover:text-neutral-950 active:scale-[0.98] dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <LightMode sx={{ width: 20, height: 20 }} />
              ) : (
                <DarkMode sx={{ width: 20, height: 20 }} />
              )}
            </button>
          </nav>

          {/* Mobile menu and theme toggle */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="rounded-full border border-neutral-200 bg-white p-2 text-neutral-600 shadow-sm transition-smooth hover:text-neutral-950 active:scale-[0.98] dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <LightMode sx={{ width: 20, height: 20 }} />
              ) : (
                <DarkMode sx={{ width: 20, height: 20 }} />
              )}
            </button>
            <button
              onClick={toggleMobileMenu}
              className="rounded-full border border-neutral-200 bg-white p-2 text-neutral-600 shadow-sm transition-smooth hover:text-neutral-950 active:scale-[0.98] dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100"
              aria-label="Toggle navigation"
            >
              {mobileMenuOpen ? (
                <Close sx={{ width: 24, height: 24 }} />
              ) : (
                <Menu sx={{ width: 24, height: 24 }} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-neutral-200 bg-stone-50/95 backdrop-blur-xl animate-slide-up dark:border-neutral-800 dark:bg-neutral-950/95">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <button
              onClick={() => {
                onHomeClick();
                setMobileMenuOpen(false);
              }}
              className="block w-full rounded-xl px-4 py-3 text-left text-neutral-600 transition-smooth hover:bg-white hover:text-neutral-950 dark:text-neutral-400 dark:hover:bg-neutral-900 dark:hover:text-neutral-100"
            >
              Home
            </button>
            <button
              onClick={() => {
                onAboutClick();
                setMobileMenuOpen(false);
              }}
              className="block w-full rounded-xl px-4 py-3 text-left text-neutral-600 transition-smooth hover:bg-white hover:text-neutral-950 dark:text-neutral-400 dark:hover:bg-neutral-900 dark:hover:text-neutral-100"
            >
              About
            </button>
            {isLoggedIn && (
              <button
                onClick={() => {
                  onAdminClick();
                  setMobileMenuOpen(false);
                }}
                className="block w-full rounded-xl px-4 py-3 text-left text-neutral-600 transition-smooth hover:bg-white hover:text-neutral-950 dark:text-neutral-400 dark:hover:bg-neutral-900 dark:hover:text-neutral-100"
              >
                Admin
              </button>
            )}
            {isLoggedIn && onSignOut && (
              <button
                onClick={() => {
                  onSignOut();
                  setMobileMenuOpen(false);
                }}
                className="block w-full rounded-xl px-4 py-3 text-left text-red-600 transition-smooth hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-950/30 dark:hover:text-red-300"
              >
                Logout
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
